import React, { useCallback, useMemo, useReducer } from 'react';
import { Box, Typography } from '@mui/material';

import { Button } from '../../../components/core';
import { AGENT_HUB_CONTENT_MAX_WIDTH } from '../ellaFormStyles';
import { useMerchant } from '../../../contexts/MerchantContext';
import { useGetMerchantQuery } from '../../../store/modules/merchant/api';
import { OnboardingDraftProvider, useOnboardingDraft } from './OnboardingDraftContext';
import { AgentSetupStep } from './steps/AgentSetupStep';
import { GuardrailsStep } from './steps/GuardrailsStep';
import { PublishStep } from './steps/PublishStep';
import { ReadinessCheckStep } from './steps/ReadinessCheckStep';
import { TemplateStep } from './steps/TemplateStep';
import {
  canAdvanceFromOnboardingStep,
  getYourStoreError,
  YourStoreStep,
  YourStoreValidationProvider,
} from './steps/YourStoreStep';
import { SQUARESPACE_PLATFORM_ID } from './platforms';
import { useSquarespaceStoreLink } from './useSquarespaceStoreLink';

export type OnboardingStepProps = {
  stepIndex: number;
  totalSteps: number;
};

type EllaOnboardingStep = {
  id: string;
  title: string;
  heading: string;
  description: string;
  dataTestId: string;
  /** Publish uses a custom success layout instead of the shared step chrome. */
  customLayout?: boolean;
};

const ELLA_ONBOARDING_STEPS = [
  {
    id: 'template',
    title: 'Template',
    heading: 'Choose a template',
    description:
      'Pick the primary job for this agent. You can add more agents later in the same vertical.',
    dataTestId: 'ella-onboarding-template-step',
  },
  {
    id: 'your-store',
    title: 'Your store',
    heading: 'Connect your store',
    description:
      'Connect your ecommerce platform and choose the knowledge sources your agent should learn from.',
    dataTestId: 'ella-onboarding-your-store-step',
  },
  {
    id: 'agent-setup',
    title: 'Agent set up',
    heading: 'Configure your AI concierge',
    description:
      'Fine-tune the name, greeting, suggested prompts, and branding shoppers see in chat.',
    dataTestId: 'ella-onboarding-agent-setup-step',
  },
  {
    id: 'guardrails',
    title: 'Guardrails',
    heading: 'Set guardrails',
    description: 'Define the rules your agent must follow. Core protections are always on.',
    dataTestId: 'ella-onboarding-guardrails-step',
  },
  {
    id: 'readiness-check',
    title: 'Readiness check',
    heading: 'Readiness check',
    description:
      'Add the knowledge your agent needs to answer common shopper questions. Each entry is saved as a workflow rule.',
    dataTestId: 'ella-onboarding-readiness-check-step',
  },
  {
    id: 'publish',
    title: 'Publish',
    heading: 'Save your agent configurations',
    description: 'Agent is configured, guardrails are on, and your storefront setup is saved.',
    dataTestId: 'ella-onboarding-publish-step',
    customLayout: true,
  },
] as const satisfies readonly EllaOnboardingStep[];

const ELLA_ONBOARDING_STEP_COUNT = ELLA_ONBOARDING_STEPS.length;

type EllaOnboardingStepId = (typeof ELLA_ONBOARDING_STEPS)[number]['id'];

type OnboardingStepComponent = () => JSX.Element;

const OnboardingStepFrame = ({
  stepIndex,
  totalSteps,
  heading,
  description,
  dataTestId,
  children,
}: OnboardingStepProps & {
  heading: string;
  description: string;
  dataTestId: string;
  children?: React.ReactNode;
}): JSX.Element => (
  <Box data-testid={dataTestId}>
    <Typography
      component="p"
      variant="small"
      color="link.main"
      fontWeight="semibold"
      sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: '0.6px' }}
    >
      Step {stepIndex + 1} of {totalSteps}
    </Typography>

    <Typography component="h2" variant="xxl" sx={{ mb: 1 }}>
      {heading}
    </Typography>

    <Typography variant="medium" color="text.secondary" sx={{ mb: 4, display: 'block' }}>
      {description}
    </Typography>

    {children}
  </Box>
);

const ONBOARDING_STEP_COMPONENTS: Record<
  Exclude<EllaOnboardingStepId, 'publish'>,
  OnboardingStepComponent
> = {
  template: TemplateStep,
  'your-store': YourStoreStep,
  'agent-setup': AgentSetupStep,
  guardrails: GuardrailsStep,
  'readiness-check': ReadinessCheckStep,
};

const CIRCLE_SIZE = 32;
const CONNECTOR_HEIGHT = 2;
const CONNECTOR_TOP = CIRCLE_SIZE / 2 - CONNECTOR_HEIGHT / 2;

type ConnectorLineStyle = {
  left: string;
  width: string;
};

const getStepCenterPercent = (stepIndex: number, stepCount: number): number =>
  ((stepIndex + 0.5) / stepCount) * 100;

const getConnectorTrackStyle = (stepCount: number): ConnectorLineStyle => {
  if (stepCount <= 1) {
    return { left: '0%', width: '100%' };
  }

  const firstCenterPercent = getStepCenterPercent(0, stepCount);
  const lastCenterPercent = getStepCenterPercent(stepCount - 1, stepCount);

  return {
    left: `${firstCenterPercent}%`,
    width: `${lastCenterPercent - firstCenterPercent}%`,
  };
};

const getConnectorProgressStyle = (
  activeStepIndex: number,
  stepCount: number,
): ConnectorLineStyle => {
  if (stepCount <= 1) {
    return { left: '0%', width: '100%' };
  }

  const firstCenterPercent = getStepCenterPercent(0, stepCount);
  const lastCenterPercent = getStepCenterPercent(stepCount - 1, stepCount);
  const trackWidth = lastCenterPercent - firstCenterPercent;

  if (activeStepIndex <= 0) {
    return { left: `${firstCenterPercent}%`, width: '0%' };
  }

  if (activeStepIndex >= stepCount - 1) {
    return {
      left: `${firstCenterPercent}%`,
      width: `${trackWidth}%`,
    };
  }

  const activeCenterPercent = getStepCenterPercent(activeStepIndex, stepCount);

  return {
    left: `${firstCenterPercent}%`,
    width: `${activeCenterPercent - firstCenterPercent}%`,
  };
};

const OnboardingStepNav = ({
  steps,
  activeStepIndex,
}: {
  steps: readonly EllaOnboardingStep[];
  activeStepIndex: number;
}): JSX.Element => {
  const connectorTrack = getConnectorTrackStyle(steps.length);
  const connectorProgress = getConnectorProgressStyle(activeStepIndex, steps.length);

  return (
    <Box
      component="nav"
      aria-label="Onboarding steps"
      data-testid="ella-onboarding-step-nav"
      sx={{
        width: '100%',
        overflowX: 'auto',
        pb: 2,
      }}
    >
      <Box
        component="ol"
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          listStyle: 'none',
          m: 0,
          p: 0,
          width: '100%',
          minWidth: { xs: 560, sm: '100%' },
        }}
      >
        <Box
          aria-hidden
          data-testid="ella-onboarding-step-nav-connector"
          sx={{
            position: 'absolute',
            top: CONNECTOR_TOP,
            left: connectorTrack.left,
            width: connectorTrack.width,
            height: CONNECTOR_HEIGHT,
            bgcolor: 'secondary.main',
            zIndex: 0,
          }}
        />
        <Box
          aria-hidden
          data-testid="ella-onboarding-step-nav-connector-progress"
          sx={{
            position: 'absolute',
            top: CONNECTOR_TOP,
            left: connectorProgress.left,
            height: CONNECTOR_HEIGHT,
            width: connectorProgress.width,
            bgcolor: 'brand.main',
            zIndex: 0,
          }}
        />

        {steps.map((step, index) => {
          const isActive = index === activeStepIndex;
          const isReached = index <= activeStepIndex;

          return (
            <Box
              component="li"
              key={step.id}
              data-testid={`ella-onboarding-step-nav-${step.id}`}
              data-active={isActive ? 'true' : 'false'}
              data-reached={isReached ? 'true' : 'false'}
              aria-current={isActive ? 'step' : undefined}
              sx={{
                position: 'relative',
                zIndex: 1,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: 72,
                px: 0.5,
              }}
            >
              <Box
                aria-hidden
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: CIRCLE_SIZE,
                  height: CIRCLE_SIZE,
                  borderRadius: '50%',
                  border: '1px solid',
                  borderColor: isReached ? 'brand.main' : 'secondary.main',
                  bgcolor: isReached ? 'brand.main' : 'background.paper',
                  color: isReached ? 'brand.contrastText' : 'text.secondary',
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {index + 1}
              </Box>

              <Typography
                component="span"
                variant="small"
                fontWeight={isActive ? 'semibold' : 'regular'}
                sx={{
                  mt: 1,
                  textAlign: 'center',
                  color: isActive ? 'text.primary' : 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                {step.title}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const OnboardingStepContent = ({
  step,
  stepIndex,
  totalSteps,
}: OnboardingStepProps & { step: EllaOnboardingStep }): JSX.Element => {
  const StepComponent = ONBOARDING_STEP_COMPONENTS[step.id as EllaOnboardingStepId];

  if (step.customLayout) {
    return <PublishStep stepIndex={stepIndex} totalSteps={totalSteps} />;
  }

  return (
    <OnboardingStepFrame
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      heading={step.heading}
      description={step.description}
      dataTestId={step.dataTestId}
    >
      <StepComponent />
    </OnboardingStepFrame>
  );
};

type WizardState = {
  activeStepIndex: number;
};

type WizardAction = { type: 'next' } | { type: 'back' };

const clampStepIndex = (index: number): number =>
  Math.min(Math.max(index, 0), ELLA_ONBOARDING_STEP_COUNT - 1);

const wizardReducer = (state: WizardState, action: WizardAction): WizardState => {
  switch (action.type) {
    case 'next':
      return { activeStepIndex: clampStepIndex(state.activeStepIndex + 1) };
    case 'back':
      return { activeStepIndex: clampStepIndex(state.activeStepIndex - 1) };
    default:
      return state;
  }
};

const useOnboardingWizard = () => {
  const [{ activeStepIndex }, dispatch] = useReducer(wizardReducer, { activeStepIndex: 0 });

  const step = ELLA_ONBOARDING_STEPS[activeStepIndex];
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === ELLA_ONBOARDING_STEP_COUNT - 1;

  const goNext = useCallback(() => {
    dispatch({ type: 'next' });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: 'back' });
  }, []);

  return useMemo(
    () => ({
      activeStepIndex,
      step,
      totalSteps: ELLA_ONBOARDING_STEP_COUNT,
      isFirstStep,
      isLastStep,
      goNext,
      goBack,
    }),
    [activeStepIndex, step, isFirstStep, isLastStep, goNext, goBack],
  );
};

const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  let current = element?.parentElement ?? null;
  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const scrollWizardToTop = (element: HTMLElement | null): void => {
  const scrollParent = getScrollParent(element);
  if (scrollParent) {
    scrollParent.scrollTop = 0;
    return;
  }
  window.scrollTo({ top: 0, left: 0 });
};

const EllaOnboardingContent = (): JSX.Element => {
  const { merchantId } = useMerchant();
  const {
    data: merchant,
    isUninitialized,
    isLoading,
    isFetching,
    isError,
  } = useGetMerchantQuery(merchantId, { skip: !merchantId });
  const { activeStepIndex, step, totalSteps, isFirstStep, isLastStep, goNext, goBack } =
    useOnboardingWizard();
  const { draft } = useOnboardingDraft();
  const [yourStoreShowErrors, setYourStoreShowErrors] = React.useState(false);
  const isMerchantLoading =
    Boolean(merchantId) && !isError && (isUninitialized || isLoading || isFetching);
  const { link: squarespaceLink, isError: isSquarespaceLinkError } = useSquarespaceStoreLink();
  const isSquarespaceLinkLoading =
    draft.store.platformId === SQUARESPACE_PLATFORM_ID &&
    !squarespaceLink &&
    !isSquarespaceLinkError;
  const storeContext = {
    merchantExternalPlatformId: merchant?.externalPlatformId,
    squarespaceLink,
  };
  const stepIsValid = canAdvanceFromOnboardingStep(step.id, draft, storeContext);
  const canContinue = step.id === 'your-store' ? true : stepIsValid;
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    scrollWizardToTop(rootRef.current);
  }, [activeStepIndex]);

  React.useEffect(() => {
    if (step.id !== 'your-store') {
      setYourStoreShowErrors(false);
    }
  }, [step.id]);

  const handleContinue = () => {
    if (step.id === 'your-store') {
      // A connected Squarespace site can only be validated once the link loads.
      if (isSquarespaceLinkLoading) {
        return;
      }
      const formatError = getYourStoreError(draft.store, { squarespaceLink });
      if (formatError) {
        setYourStoreShowErrors(true);
        return;
      }
      if (isMerchantLoading) {
        return;
      }
      if (getYourStoreError(draft.store, storeContext)) {
        setYourStoreShowErrors(true);
        return;
      }
    } else if (!stepIsValid) {
      return;
    }

    setYourStoreShowErrors(false);
    goNext();
  };

  return (
    <YourStoreValidationProvider showErrors={yourStoreShowErrors}>
      <Box
        ref={rootRef}
        data-testid="ELLA_ONBOARDING"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          width: '100%',
          height: '100%',
          minHeight: 0,
          py: 6,
        }}
      >
        <Box
          sx={{
            width: '100%',
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <OnboardingStepNav steps={ELLA_ONBOARDING_STEPS} activeStepIndex={activeStepIndex} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            maxWidth: AGENT_HUB_CONTENT_MAX_WIDTH,
            width: '100%',
            mx: 'auto',
            px: 3,
          }}
        >
          <Box
            component="section"
            aria-label={step.title}
            data-testid={`ella-onboarding-step-${step.id}`}
            sx={{ flexGrow: 1, minHeight: 240, pt: 3 }}
          >
            <OnboardingStepContent
              step={step}
              stepIndex={activeStepIndex}
              totalSteps={totalSteps}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              pt: 4,
              pb: 6,
            }}
          >
            <Button
              theme="secondary"
              text="Back"
              onClick={goBack}
              disabled={isFirstStep}
              dataTestId="ella-onboarding-back"
              sx={{ minWidth: 120, height: 48 }}
            />

            {isLastStep ? (
              <Box sx={{ minWidth: 120 }} />
            ) : (
              <Button
                theme="primary"
                text="Continue"
                onClick={handleContinue}
                disabled={step.id === 'your-store' ? isMerchantLoading : !canContinue}
                dataTestId="ella-onboarding-next"
                sx={{ minWidth: 120, height: 48 }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </YourStoreValidationProvider>
  );
};

export const EllaOnboarding = (): JSX.Element => (
  <OnboardingDraftProvider>
    <EllaOnboardingContent />
  </OnboardingDraftProvider>
);
